import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Text } from '@visx/text';

import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import ImageRequest from '@Containers/ImageRequest';
import { useWithHover } from '@Hooks/withOnHoverStuff';
import SubstanceLeafNodeAdornment from './SubstanceLeafNodeAdornment';
import StructureButtons from './StructureButtons';

import './SubstanceNode.scss';

const STRUCTURE_IMAGE_HEIGHT = 130; // Original 150
const STRUCTURE_IMAGE_WIDTH = 169; // Original 195

export const SUBSTANCE_HEIGHT = STRUCTURE_IMAGE_HEIGHT + 44; // paddings: 8 from top, 36 from bottom (for buttons)
export const SUBSTANCE_WIDTH = STRUCTURE_IMAGE_WIDTH + 32; // paddings: 16 from left and right
export const SUBSTANCE_HALF_HEIGHT = SUBSTANCE_HEIGHT / 2;
export const SUBSTANCE_HALF_WIDTH = SUBSTANCE_WIDTH / 2;

const SUBSTANCE_BORDER = 2;
const SUBSTANCE_BORDER_HEIGHT = SUBSTANCE_HEIGHT - SUBSTANCE_BORDER - 1;
const SUBSTANCE_BORDER_WIDTH = SUBSTANCE_WIDTH - SUBSTANCE_BORDER - 1;

const structureImage = {
  height: STRUCTURE_IMAGE_HEIGHT,
  width: STRUCTURE_IMAGE_WIDTH,
  x: 16, // padding from left
  y: 8, // padding from top
};

const structureImageLabel = {
  x: 16 + STRUCTURE_IMAGE_WIDTH / 2,
  y: 8 + STRUCTURE_IMAGE_HEIGHT / 2,
  width: STRUCTURE_IMAGE_WIDTH * 0.7,
  textAnchor: 'middle',
  verticalAnchor: 'middle',
};

const shiftNode = (width, height) => ({
  x: -width / 2,
  y: -height / 2,
});

/* eslint-disable react-hooks/exhaustive-deps */
export default function SubstanceNode({
  orientation,
  imageUrl,
  imageLabel,
  onOptionClick,
  checkShoppingCartVisibility,
  isCurrentStep,
  buttonsDisabled,
  isZoomAvailable,
  hasAddNodeIcon,
  addNodeEnabled,
  onAddNodeClick,
  supplierNumber,
  viewDetailsEnabled,
  optionsEnabled,
  substanceDetails,
  onHoverNode,
  highlightedImageUrl,
  customerInfo,
}) {
  const { x, y } = shiftNode(SUBSTANCE_WIDTH, SUBSTANCE_HEIGHT);
  const [shoppingCartVisible, setShoppingCartVisibility] = useState(false);
  const [isHovered, onMouseEnterHandler, onMouseLeaveHandler] = useWithHover();

  const checkVisibility = async () => {
    const result = await checkShoppingCartVisibility();
    setShoppingCartVisibility(!!result);
  };

  useEffect(() => {
    checkVisibility();
  }, []);

  useEffect(() => {
    onHoverNode(isHovered);
  }, [isHovered]);

  const imageGenerationRequestsLimit = Auth.isFeatureEnabled(FEATURES.image_generation_requests_limit);

  const actualImgUrl = highlightedImageUrl || imageUrl;

  return (
    <>
      <svg
        className={cn('rt-project-route-substance-node', { 'rt-project-route-substance-node--buttons-disabled': buttonsDisabled })}
        height={SUBSTANCE_HEIGHT}
        width={SUBSTANCE_WIDTH}
        x={x}
        y={y}>
        <rect
          strokeWidth={SUBSTANCE_BORDER}
          className={cn(
            'rt-project-route-substance-node--border',
            { 'rt-project-route-substance-node--border--commercially-available': shoppingCartVisible && !isCurrentStep },
            { 'rt-project-route-substance-node--border--active-step': isCurrentStep }
          )}
          x={SUBSTANCE_BORDER}
          y={SUBSTANCE_BORDER}
          height={SUBSTANCE_BORDER_HEIGHT}
          width={SUBSTANCE_BORDER_WIDTH}
        />
        {/* Content rendering based on available data */}
        {(() => {
          if (actualImgUrl) {
            return (
              <g
                className="rt-project-route-substance-node--image-group"
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}>
                {imageGenerationRequestsLimit ? (
                  <ImageRequest
                    url={actualImgUrl}
                    limit={parseInt(imageGenerationRequestsLimit, 10)}
                    render={({ src }) => (
                      <image
                        className="rt-project-route-substance-node--image"
                        x={structureImage.x}
                        y={structureImage.y}
                        xlinkHref={src}
                        height={structureImage.height}
                        width={structureImage.width}
                        alt="chemical structure"
                      />
                    )}
                  />
                ) : (
                  <image
                    className="rt-project-route-substance-node--image"
                    x={structureImage.x}
                    y={structureImage.y}
                    xlinkHref={actualImgUrl}
                    height={structureImage.height}
                    width={structureImage.width}
                    alt="chemical structure"
                  />
                )}
              </g>
            );
          }

          if (imageLabel) {
            return (
              <Text
                className="rt-project-route-substance-node--image-label"
                x={structureImageLabel.x}
                y={structureImageLabel.y}
                width={structureImageLabel.width}
                textAnchor={structureImageLabel.textAnchor}
                verticalAnchor={structureImageLabel.verticalAnchor}>
                {imageLabel}
              </Text>
            );
          }

          return null;
        })()}
        {!buttonsDisabled && (
          <StructureButtons
            substanceHeight={SUBSTANCE_HEIGHT}
            shoppingCartVisible={shoppingCartVisible}
            onOptionClick={onOptionClick}
            isZoomAvailable={isZoomAvailable}
            supplierNumber={supplierNumber}
            viewDetailsEnabled={viewDetailsEnabled}
            optionsEnabled={optionsEnabled}
            substanceDetails={substanceDetails}
            customerInfo={customerInfo}
          />
        )}
      </svg>
      {hasAddNodeIcon && (
        <SubstanceLeafNodeAdornment
          nodeX={x}
          nodeY={y}
          nodeWidth={SUBSTANCE_WIDTH}
          nodeHeight={SUBSTANCE_HEIGHT}
          orientation={orientation}
          onClick={onAddNodeClick}
          disabled={!addNodeEnabled}
        />
      )}
    </>
  );
}
